import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { useIdleLogout } from '../hooks/useIdleLogout'
import { OnlineIndicator } from './ui/OnlineIndicator'
import {
  LayoutDashboard,
  Building2,
  Ticket,
  Settings,
  Building,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Moon,
  Sun,
  CreditCard,
  ShieldCheck,
  Megaphone,
  KeyRound,
  Shield,
  ShieldAlert,
  Network
} from 'lucide-react'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Organizations', href: '/organizations', icon: Network },
  { name: 'Centri', href: '/tenants', icon: Building2 },
  { name: 'Piani', href: '/plans', icon: CreditCard },
  { name: 'Coupon', href: '/coupons', icon: Ticket },
  { name: 'Annunci', href: '/announcements', icon: Megaphone },
  { name: 'Gestione 2FA', href: '/two-factor', icon: KeyRound },
  { name: 'Stripe', href: '/stripe', icon: Shield },
  { name: 'Stato Sicurezza', href: '/security', icon: ShieldAlert },
  { name: 'Log Accessi', href: '/logs', icon: ShieldCheck },
  { name: 'Impostazioni', href: '/settings', icon: Settings },
  { name: 'Dati Aziendali', href: '/company', icon: Building },
  { name: 'Documenti Legali', href: '/legal-documents', icon: FileText },
]

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const { user, logout } = useAuth()
  const { darkMode, toggleDarkMode } = useTheme()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Logout automatico dopo 30 minuti di inattività
  useIdleLogout(handleLogout, 30 * 60 * 1000)

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Indicatore Online/Offline */}
      <OnlineIndicator />

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-primary dark:bg-gray-800 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/20 dark:border-gray-700">
          <div className="flex items-center">
            <img 
              src="/logo.png" 
              alt="Il Diario di Bordo" 
              className="h-16 w-16 object-contain"
            />
            <span className="ml-2 text-lg sm:text-xl font-bold text-white truncate">Il Diario di Bordo</span>
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-white/20 text-white rounded">
              Admin
            </span>
          </div>
          <button
            className="lg:hidden text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          <div className="space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Bottom user section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/20 dark:border-gray-700">
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `flex items-center text-white/70 hover:text-white transition-colors ${
                isActive ? 'text-white' : ''
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <User className="h-4 w-4 text-white" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-white">{user?.nome} {user?.cognome}</p>
              <p className="text-xs text-white/60">{user?.email}</p>
            </div>
          </NavLink>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white dark:bg-gray-800 shadow-sm">
          <div className="flex items-center justify-between h-16 px-4">
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 mr-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              title={darkMode ? 'Tema chiaro' : 'Tema scuro'}
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                </div>
                <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {user?.nome}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.nome} {user?.cognome}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                    </div>
                    <NavLink
                      to="/profile"
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Profilo
                    </NavLink>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Esci
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
